import { extractJsonObject } from "../outline/outlinePrompt.js";
import { SURF_MAX_BULLETS_PER_SLIDE } from "../layout/designerHeuristics.js";
export const SURF_CONTENT_POLISH_SYSTEM = [
    "You are an editorial polish agent for professional presentation decks (Surf).",
    "Rewrite copy to be sharper: strong short titles, parallel concise bullets, one idea per slide.",
    "Preserve the SOURCE LANGUAGE of the input (do not translate).",
    "NEVER invent facts, statistics, names, or numbers not present in the input.",
    "You may rephrase, shorten, deduplicate, and emphasize existing numbers for stat slides.",
    `Max ${SURF_MAX_BULLETS_PER_SLIDE} bullets per slide; remove filler and walls of text.`,
    "Return ONLY JSON, no markdown.",
].join(" ");
export function extractPolishPayloadFromSlide(slide) {
    return {
        role: slide.outlineRole,
        layout: slide.layout,
        speakerNotes: slide.speakerNotes,
        slots: slide.layers
            .filter((l) => l.type === "text")
            .map((l) => ({
            slotRole: String(l.style?.slotRole ?? l.textRole ?? "body"),
            content: l.content,
        })),
    };
}
export function extractPolishPayloadFromOutlineSection(section) {
    return {
        id: section.id,
        role: section.role,
        title: section.title,
        subtitle: section.subtitle,
        bullets: [...section.bullets],
        body: section.body,
        notes: section.notes,
    };
}
export function polishSlideUserPrompt(payload) {
    return [
        "Polish this single slide copy. Respond with JSON:",
        '{"slots":[{"slotRole":"title","content":"..."}],"speakerNotes":"..."}',
        "Keep the same slotRole keys as input.",
        "",
        JSON.stringify(payload, null, 2),
    ].join("\n");
}
export function polishOutlineUserPrompt(sections) {
    return [
        "Polish these outline sections. Respond with JSON:",
        '{"sections":[{"id":"...","title":"...","subtitle":"...","bullets":["..."],"body":"...","notes":"..."}]}',
        "Keep section ids unchanged. Do not add or remove sections.",
        "",
        JSON.stringify({ sections }, null, 2),
    ].join("\n");
}
function normalizeBullets(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw
        .map((b) => String(b ?? "").trim())
        .filter(Boolean)
        .slice(0, SURF_MAX_BULLETS_PER_SLIDE);
}
export function parsePolishedSlideResponse(raw) {
    const obj = extractJsonObject(raw);
    if (!obj)
        throw new Error("Risposta polish slide non JSON.");
    const slotsRaw = obj.slots;
    if (!Array.isArray(slotsRaw) || !slotsRaw.length) {
        throw new Error("JSON polish slide incompleto (slots).");
    }
    const slots = slotsRaw
        .map((item) => {
        const row = item;
        const slotRole = String(row.slotRole ?? row.role ?? "").trim();
        const content = String(row.content ?? row.text ?? "").trim();
        if (!slotRole || !content)
            return null;
        return { slotRole, content };
    })
        .filter((x) => x !== null);
    if (!slots.length)
        throw new Error("Nessuno slot valido nella risposta polish.");
    const speakerNotes = String(obj.speakerNotes ?? obj.notes ?? "").trim() || undefined;
    return { slots, speakerNotes };
}
export function parsePolishedOutlineResponse(raw, expectedIds) {
    const obj = extractJsonObject(raw);
    if (!obj)
        throw new Error("Risposta polish outline non JSON.");
    const sectionsRaw = obj.sections;
    if (!Array.isArray(sectionsRaw))
        throw new Error("JSON polish outline incompleto (sections).");
    const byId = new Map();
    for (const item of sectionsRaw) {
        const row = item;
        const id = String(row.id ?? "").trim();
        const title = String(row.title ?? "").trim();
        if (!id || !title)
            continue;
        byId.set(id, {
            id,
            title,
            subtitle: String(row.subtitle ?? "").trim() || undefined,
            bullets: normalizeBullets(row.bullets),
            body: String(row.body ?? "").trim() || undefined,
            notes: String(row.notes ?? "").trim() || undefined,
        });
    }
    const result = [];
    for (const id of expectedIds) {
        const polished = byId.get(id);
        if (!polished)
            throw new Error(`Sezione polish mancante: ${id}`);
        result.push(polished);
    }
    return result;
}
export function applyPolishToSlide(slide, polished) {
    const bySlot = new Map(polished.slots.map((s) => [s.slotRole, s.content]));
    return {
        ...slide,
        speakerNotes: polished.speakerNotes ?? slide.speakerNotes,
        layers: slide.layers.map((layer) => {
            if (layer.type !== "text")
                return layer;
            const slotRole = String(layer.style?.slotRole ?? layer.textRole ?? "body");
            const next = bySlot.get(slotRole);
            if (next === undefined)
                return layer;
            return { ...layer, content: next };
        }),
    };
}
export function applyPolishToOutlineSections(sections, polished) {
    const byId = new Map(polished.map((p) => [p.id, p]));
    return sections.map((section) => {
        const next = byId.get(section.id);
        if (!next)
            return section;
        return {
            ...section,
            title: next.title,
            subtitle: next.subtitle,
            bullets: next.bullets,
            body: next.body,
            notes: next.notes,
        };
    });
}
